/**
 * Deciding which entries in a dropped zip are actually résumés.
 *
 * This is where a bulk import is won or lost, and it had no tests of any kind while it sat inside
 * an 865-line component. A zip made by right-clicking a folder on a Mac is the ordinary input here,
 * and it carries a `__MACOSX/` tree, an AppleDouble `._name` twin for every real file, and a
 * `.DS_Store`. Let those through and every second "résumé" sent for extraction is a few hundred
 * bytes of Finder metadata, which costs an OpenAI call each to discover.
 *
 * Extracted so the rules can be stated and checked without a browser, a zip fixture on disk, or a
 * running app.
 */
export const ALLOWED_EXT = ['.pdf', '.docx'];

/** Finder and Explorer leavings that are never a résumé. */
const METADATA_NAMES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini']);

/** The file-type bits of a unix mode, and the value meaning "symlink". */
const S_IFMT = 0o170000;
const S_IFLNK = 0o120000;

export interface ZipEntryLike {
  dir: boolean;
  /** JSZip spells this differently across versions and archive types; all three are checked. */
  unixPermissions?: number | null;
  unixPerms?: number | null;
  externalFileAttributes?: number | null;
}

/**
 * The basename this entry should be imported as, or null to skip it.
 *
 * Folder structure is deliberately flattened: two candidates filed under different folders are
 * still two résumés, and the key that reaches S3 carries only the name.
 */
export function resumeNameForEntry(path: string, entry: ZipEntryLike): string | null {
  if (entry.dir) return null;

  // Some zippers write backslashes. Normalise before anything looks at path segments.
  const normPath = path.replace(/\\/g, '/');

  if (normPath.startsWith('__MACOSX/') || normPath.includes('/__MACOSX/')) return null;

  const parts = normPath.split('/');
  let baseName = parts[parts.length - 1];
  if (!baseName) return null;

  /*
   * NFC, because macOS stores filenames decomposed. "Renée.pdf" zipped on a Mac arrives as
   * "Renée.pdf" and compares unequal to the same name typed anywhere else, so without this
   * the duplicate check misses and the same person is imported twice.
   */
  try {
    baseName = baseName.normalize('NFC');
  } catch {
    // Older engines without full Unicode support: the name is still usable, just not normalised.
  }

  const lowerBase = baseName.toLowerCase();

  // AppleDouble twins and dotfiles. `._x` is checked first only for readability; `.` covers it.
  if (lowerBase.startsWith('._') || lowerBase.startsWith('.')) return null;
  if (METADATA_NAMES.has(lowerBase)) return null;

  const perms = entry.unixPermissions ?? entry.unixPerms ?? entry.externalFileAttributes;
  if (typeof perms === 'number' && (perms & S_IFMT) === S_IFLNK) return null;

  const extParts = baseName.split('.');
  const ext = extParts.length > 1 ? `.${extParts[extParts.length - 1]}` : '';
  if (!ALLOWED_EXT.includes(ext.toLowerCase())) return null;

  return baseName;
}

/**
 * Every résumé in the zip, deduplicated by name, in no guaranteed order.
 *
 * Reading an entry can throw on a corrupt archive; that entry is dropped and the rest of the import
 * continues, because one unreadable file in a folder of two hundred should not cost the other
 * hundred and ninety-nine.
 */
export async function extractFilesFromZip(
  file: Blob,
): Promise<{ name: string; buffer: ArrayBuffer }[]> {
  const JSZipMod = await import('jszip');
  const zip = new JSZipMod.default();
  const loaded = await zip.loadAsync(file);

  const files: { name: string; buffer: ArrayBuffer }[] = [];
  const seen = new Set<string>();

  await Promise.all(
    Object.keys(loaded.files).map(async (path) => {
      const entry = loaded.files[path] as unknown as ZipEntryLike;
      const name = resumeNameForEntry(path, entry);
      if (!name) return;

      const lower = name.toLowerCase();
      // Claimed before the read, so two entries with one name cannot both decompress.
      if (seen.has(lower)) return;
      seen.add(lower);

      try {
        const buffer = await (
          loaded.files[path] as unknown as { async(t: string): Promise<ArrayBuffer> }
        ).async('arraybuffer');
        files.push({ name, buffer });
      } catch (err) {
        // Dropped, and the name is released so a later readable entry with the same name can win.
        seen.delete(lower);
        console.warn('Failed to read zip entry', path, err);
      }
    }),
  );

  return files;
}
